import axios from 'axios';
import { API_CONFIG } from '../config/api';

export const TOKEN_KEY = 'token';
export const REFRESH_KEY = 'refresh_token';
export const USER_GIVEN_NAME_KEY = 'user_given_name';
export const USER_FAMILY_NAME_KEY = 'user_family_name';

/** @param {string} accessToken */
function decodeJwtPayload(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const json = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Reads `given_name` / `family_name` from the access JWT (Keycloak). Falls back to splitting `name`.
 * @param {string} accessToken
 */
export function persistUserDisplayFromAccessToken(accessToken) {
  const payload = decodeJwtPayload(accessToken);
  let first = '';
  let last = '';
  if (payload && typeof payload === 'object') {
    if (typeof payload.given_name === 'string') first = payload.given_name.trim();
    if (typeof payload.family_name === 'string') last = payload.family_name.trim();
    if (!first && !last && typeof payload.name === 'string') {
      const bits = payload.name.trim().split(/\s+/).filter(Boolean);
      first = bits[0] || '';
      last = bits.slice(1).join(' ');
    }
  }
  if (first || last) {
    localStorage.setItem(USER_GIVEN_NAME_KEY, first);
    localStorage.setItem(USER_FAMILY_NAME_KEY, last);
  } else {
    localStorage.removeItem(USER_GIVEN_NAME_KEY);
    localStorage.removeItem(USER_FAMILY_NAME_KEY);
  }
}

/** Greeting for the wallet shell ("Welcome, First Last"). */
export function getWelcomeGreeting() {
  const first = localStorage.getItem(USER_GIVEN_NAME_KEY)?.trim() || '';
  const last = localStorage.getItem(USER_FAMILY_NAME_KEY)?.trim() || '';
  const parts = [first, last].filter(Boolean);
  if (parts.length === 0) return 'Welcome';
  return `Welcome, ${parts.join(' ')}`;
}

export const LOGIN_RETURN_PATH_SESSION_KEY = 'wallet_dashboard_login_return';

function pathOnlyNoQueryHash(path) {
  const q = path.indexOf('?');
  const h = path.indexOf('#');
  let end = path.length;
  if (q >= 0) end = Math.min(end, q);
  if (h >= 0) end = Math.min(end, h);
  return path.slice(0, end);
}

/** Same-origin relative path only; blocks `/login` and protocol-relative URLs. */
export function isSafeAppReturnPath(path) {
  if (typeof path !== 'string') return false;
  const t = path.trim();
  if (!t.startsWith('/') || t.startsWith('//')) return false;
  const base = pathOnlyNoQueryHash(t);
  if (base === '/login' || base.startsWith('/login/')) return false;
  return true;
}

/**
 * Before `window.location.assign('/login')`, store where to go after sign-in (session-only).
 * @param {string} pathnameSearchHash e.g. `location.pathname + location.search + location.hash`
 */
export function stashLoginReturnPath(pathnameSearchHash) {
  if (!isSafeAppReturnPath(pathnameSearchHash)) return;
  try {
    sessionStorage.setItem(LOGIN_RETURN_PATH_SESSION_KEY, pathnameSearchHash);
  } catch {
    /* quota / private mode */
  }
}

/** Read and clear stashed path from a hard redirect to login. */
export function consumeLoginReturnPath() {
  try {
    const v = sessionStorage.getItem(LOGIN_RETURN_PATH_SESSION_KEY);
    sessionStorage.removeItem(LOGIN_RETURN_PATH_SESSION_KEY);
    return v && isSafeAppReturnPath(v) ? v : null;
  } catch {
    return null;
  }
}

/** @param {unknown} from — React Router `location` saved as `Navigate` state */
export function returnPathFromRouterState(from) {
  if (!from || typeof from !== 'object') return null;
  const pathname = from.pathname;
  if (typeof pathname !== 'string') return null;
  const search = typeof from.search === 'string' ? from.search : '';
  const hash = typeof from.hash === 'string' ? from.hash : '';
  const full = `${pathname}${search}${hash}`;
  return isSafeAppReturnPath(full) ? full : null;
}

/** One shared refresh promise so parallel 401s do not double-call Keycloak (second call would use a blacklisted refresh). */
let refreshPromise = null;

function warnCorsOrNetwork(err, context) {
  if (process.env.NODE_ENV !== 'development') return;
  if (!axios.isAxiosError(err)) return;
  const network =
    err.code === 'ERR_NETWORK' ||
    err.message === 'Network Error' ||
    (typeof err.response?.status !== 'number' && !err.response);
  if (!network) return;
  // eslint-disable-next-line no-console
  console.warn(
    `[wallet-dashboard] ${context}: request failed before a normal HTTP response (often CORS or offline). ` +
      'For cross-origin APIs, the server must allow your origin and methods, and include Content-Type in allowed headers.'
  );
}

function apiMessage(data) {
  if (!data || typeof data !== 'object') return null;
  if (typeof data.message === 'string') return data.message;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.title === 'string') return data.title;
  if (Array.isArray(data.error) && data.error.length) {
    try {
      return JSON.stringify(data.error);
    } catch {
      return 'Validation failed';
    }
  }
  return null;
}

/**
 * `POST /api/users/loginOTP` — Farmako API returns `{ title, detail, profiles, isDummy }`;
 * each profile carries `token` (access JWT) and `refresh_token`.
 * @param {unknown} data
 * @returns {{ accessToken: string | null, refreshToken: string | null }}
 */
export function extractTokensFromLoginResponse(data) {
  if (!data || typeof data !== 'object' || !Array.isArray(data.profiles)) {
    return { accessToken: null, refreshToken: null };
  }
  const profile = data.profiles.find(
    (p) => p && typeof p === 'object' && typeof p.token === 'string' && p.token.trim() !== ''
  );
  if (!profile) {
    return { accessToken: null, refreshToken: null };
  }
  const accessToken = profile.token.trim();
  const refreshToken =
    typeof profile.refresh_token === 'string' && profile.refresh_token.trim() !== ''
      ? profile.refresh_token.trim()
      : null;
  return { accessToken, refreshToken };
}

/** Last 10 digits → E.164 India */
export function normalizeIndiaPhone(digits) {
  const d = String(digits || '').replace(/\D/g, '').slice(-10);
  if (d.length !== 10) return null;
  return `+91${d}`;
}

/**
 * @param {{ phone: string, isShort?: boolean }} body — `phone` should be E.164 (e.g. +91…)
 */
export async function sendOTP(body) {
  const response = await axios.post(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS_SEND_OTP}`,
    {
      ...body,
      isShort: typeof body.isShort === 'boolean' ? body.isShort : false,
    },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
}

/**
 * @param {{ phone: string, otp: string, isShort?: boolean }} body
 */
export async function loginWithOTP(body) {
  const response = await axios.post(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS_LOGIN_OTP}`,
    {
      ...body,
      isShort: typeof body.isShort === 'boolean' ? body.isShort : false,
    },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
}

/**
 * GET /api/users/refresh — `Authorization: Bearer` + `x-refresh-token`.
 * New tokens are read from the JSON body (`token`, `refresh_token` only).
 * @returns {Promise<{ refreshed: boolean, authRejected: boolean }>}
 */
export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = runTokenRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function runTokenRefresh() {
  const failAuth = () => {
    clearSession();
    return { refreshed: false, authRejected: true };
  };

  const access = localStorage.getItem(TOKEN_KEY);
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!access?.trim() || !refresh?.trim()) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        '[wallet-dashboard] Skipping GET /api/users/refresh: missing access or refresh token in storage. ' +
          'Ensure login returns profiles[0].token and profiles[0].refresh_token in the JSON body.'
      );
    }
    return failAuth();
  }

  try {
    const accessTrim = access.trim();
    const refreshTrim = refresh.trim();
    const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS_REFRESH}`, {
      headers: {
        Authorization: `Bearer ${accessTrim}`,
        'x-refresh-token': refreshTrim,
      },
    });
    const resData = response.data;
    /** `GET /api/users/refresh` JSON: `token`, `refresh_token`, `message`, `detail` */
    const newAccess =
      resData && typeof resData.token === 'string' ? resData.token.trim() : '';
    const newRefresh =
      resData && typeof resData.refresh_token === 'string' ? resData.refresh_token.trim() : '';

    if (!newAccess) {
      return failAuth();
    }

    localStorage.setItem(TOKEN_KEY, newAccess);
    persistUserDisplayFromAccessToken(newAccess);
    if (newRefresh) {
      localStorage.setItem(REFRESH_KEY, newRefresh);
    } else if (process.env.NODE_ENV === 'development') {
      // Old refresh is blacklisted server-side; without the new refresh token, the next rotation fails.
      // eslint-disable-next-line no-console
      console.warn(
        '[wallet-dashboard] Refresh OK but response JSON had no refresh_token; include it in the body after rotation.'
      );
    }

    return { refreshed: true, authRejected: false };
  } catch (e) {
    warnCorsOrNetwork(e, 'GET /api/users/refresh');
    const status = e?.response?.status;
    const network = axios.isAxiosError(e) && (e.code === 'ERR_NETWORK' || typeof status !== 'number');
    if (network) {
      return { refreshed: false, authRejected: false };
    }
    if (status === 401 || status === 403) {
      return failAuth();
    }
    return { refreshed: false, authRejected: false };
  }
}

export function persistSession(accessToken, refreshToken) {
  if (accessToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    persistUserDisplayFromAccessToken(accessToken);
  }
  const r = typeof refreshToken === 'string' ? refreshToken.trim() : '';
  if (r) {
    localStorage.setItem(REFRESH_KEY, r);
  } else {
    localStorage.removeItem(REFRESH_KEY);
  }
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_GIVEN_NAME_KEY);
  localStorage.removeItem(USER_FAMILY_NAME_KEY);
  try {
    sessionStorage.removeItem(LOGIN_RETURN_PATH_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export async function logoutRemote() {
  const refresh = localStorage.getItem(REFRESH_KEY);
  const refreshTrim = refresh?.trim() || '';
  try {
    await axios.put(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS_LOGOUT}`, {}, {
      headers: {
        'Content-Type': 'application/json',
        ...(refreshTrim ? { 'x-refresh-token': refreshTrim } : {}),
      },
    });
  } catch (e) {
    warnCorsOrNetwork(e, 'PUT /api/users/logout');
    /* still clear local session */
  }
  clearSession();
}

export function parseAuthError(err) {
  const data = err?.response?.data;
  return apiMessage(data) || err?.message || 'Request failed';
}

/** Same field precedence as auth/OTP errors (`message`, then `detail`, then `title`). */
export function getAxiosErrorMessage(err, fallback = 'Request failed') {
  const parsed = apiMessage(err?.response?.data);
  if (parsed) return parsed;
  if (err && typeof err.message === 'string' && err.message) return err.message;
  return fallback;
}
